import { useState, useEffect, createContext, useContext, ReactNode, createElement } from 'react';
import i18n from '../i18n';
import { authAPI } from '../services/api';
import { API_BASE_URL } from '../components/config/api';
import { Store, PlatformCredentials, AuthContextType, Employee } from '../types';

// Store session info for employee selection screen
interface StoreSession {
  storeToken: string;
  store: {
    id: number;
    store_name: string;
    address?: string;
    phone_number?: string;
    email?: string;
    warehouse?: boolean;
    eod_mode?: Store['eod_mode']; // πάντα 'manual' (migration 266) — βλ. types/index.ts
    organization_id?: number | null;
    catalog_management?: 'central' | 'independent';
    delivery?: boolean;
    delivery_self?: boolean;
    delivery_efood?: boolean;
    delivery_wolt?: boolean;
    takeaway?: boolean;
    dine_in?: boolean;
    end_of_day?: string;
    ecr_provider?: string;
    kiosk_enabled?: boolean;
    analytics_tier?: string;
    barcode_enabled?: boolean;
    waste_enabled?: boolean;
    strict_price_floor_enabled?: boolean; // απόφαση F1-W3-6 — βλ. types/index.ts (Store)
    theme?: Store['theme']; // εμφάνιση (migration 263) — ήδη στην οθόνη επιλογής υπαλλήλου/PIN
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [store, setStore] = useState<Store | null>(null);
  const [platforms, setPlatforms] = useState<PlatformCredentials[]>([]);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [storeSession, setStoreSession] = useState<StoreSession | null>(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('xentrix_token');
      if (!token) {
        // No employee token - check if we have a store session for employee selection
        const savedSession = localStorage.getItem('xentrix_store_session');
        if (savedSession) {
          try {
            const session = JSON.parse(savedSession);
            setStoreSession(session);
          } catch (e) {
            console.error('Failed to restore store session:', e);
            localStorage.removeItem('xentrix_store_session');
          }
        }
        setLoading(false);
        return;
      }

      const response = await authAPI.verifyToken();
      if (response.valid) {
        const userInfo = await authAPI.getMe();
        setStore(userInfo.store);
        setPlatforms(userInfo.platforms);
        setEmployee(userInfo.employee || null);
        setIsAuthenticated(true);
        // Set language from store settings
        const lang = userInfo.store?.language || 'el';
        i18n.changeLanguage(lang);
        localStorage.setItem('store_language', lang);
      } else {
        localStorage.removeItem('xentrix_token');
      }
    } catch (error: any) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('xentrix_token');
      localStorage.removeItem('xentrix_store_session');
      
      // Check if store was deactivated
      if (error.response?.status === 403 && error.response?.data?.error === 'Store inactive') {
        alert(error.response.data.message || 'This store has been deactivated.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Legacy login (direct employee login) - kept for backward compatibility
  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      localStorage.setItem('xentrix_token', response.token);
      setStore(response.store);
      setPlatforms(response.platforms);
      setEmployee(response.employee || null);
      setIsAuthenticated(true);
    } catch (error) {
      throw error;
    }
  };

  // New store login (Step 1) - Returns store session for employee selection
  // OR completes immediately if org_user login
  const storeLogin = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/store-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }
      
      const data = await response.json();

      // Org user login — complete immediately (no PIN step)
      if (data.loginType === 'org_user') {
        localStorage.setItem('xentrix_token', data.token);
        setEmployee(data.employee || null);
        setIsAuthenticated(true);
        return data;
      }
      
      // Store login — show employee selection
      setStoreSession({
        storeToken: data.storeToken,
        store: data.store
      });
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Employee PIN login (Step 2) - Complete authentication
  const employeeLogin = (data: any) => {
    localStorage.setItem('xentrix_token', data.token);

    // Save store session to localStorage for employee logout
    if (storeSession) {
      localStorage.setItem('xentrix_store_session', JSON.stringify(storeSession));
    }

    setStore(data.store);
    setPlatforms(data.platforms);
    setEmployee(data.employee || null);
    setStoreSession(null); // Clear store session from state (saved in localStorage)
    setIsAuthenticated(true);
    // Set language from store settings
    const lang = data.store?.language || 'el';
    i18n.changeLanguage(lang);
    localStorage.setItem('store_language', lang);
  };

  // Logout from employee (back to employee selection)
  const employeeLogout = () => {
    // Clear employee auth
    localStorage.removeItem('xentrix_token');
    setStore(null);
    setPlatforms([]);
    setEmployee(null);
    setIsAuthenticated(false);
    
    // Restore store session from localStorage
    const savedSession = localStorage.getItem('xentrix_store_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setStoreSession(session);
      } catch (e) {
        console.error('Failed to restore store session:', e);
        localStorage.removeItem('xentrix_store_session');
      }
    }
  };

  // Full logout (back to store login)
  const logout = async () => {
    // Always remove token and clear state, even if API call fails
    localStorage.removeItem('xentrix_token');
    localStorage.removeItem('xentrix_store_session');
    setStore(null);
    setPlatforms([]);
    setEmployee(null);
    setStoreSession(null);
    setIsAuthenticated(false);
    
    // Try to call logout endpoint but don't block on it
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error (token already removed):', error);
    }
  };

  const updatePlatforms = (platformName: string | 'all', patch: Partial<typeof platforms[number]>) => {
    setPlatforms(prev =>
      prev.map(p =>
        platformName === 'all' || p.platform_name === platformName
          ? { ...p, ...patch }
          : p
      )
    );
  };

  const value: AuthContextType = {
    isAuthenticated,
    store,
    platforms,
    employee,
    login,
    logout,
    loading,
    // New auth flow
    storeSession,
    storeLogin,
    employeeLogin,
    employeeLogout,
    updatePlatforms,
  };

  return createElement(AuthContext.Provider, { value }, children);
};

